// src/pages/SuperAdmin/BusinessDetailPage.tsx - Page détails business avec onglets (Design moderne avec Cards)

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Tabs,
  message,
  Switch,
  Spin,
  Breadcrumb,
  Checkbox,
  Modal,
  Card,
  Tooltip,
  Empty,
} from 'antd';
import {
  SettingOutlined,
  ShopOutlined,
  DeleteOutlined,
  EditOutlined,
  SyncOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  TrophyOutlined,
  ThunderboltOutlined,
  TagOutlined,
  UserOutlined,
  FileTextOutlined,
  DollarOutlined,
  SearchOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  EyeOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useBusiness, useUpdateBusiness, useDeleteBusiness } from '../../hooks/useBusinesses';
import {
  defaultLoyaltyPassProgram,
  normalizeLoyaltyPassProgram,
} from '../../utils/loyaltyPass';
import {
  useToggleBusinessStatus,
  useToggleLoyaltySystem,
  useMigrateClientLastVisitDate,
  useMigrateProductSellingPriceFromCost,
  useMigratePackagingUnitsDefaults,
  useRemoveRecalculateCostOnProvisioning,
  useBackfillServiceStockConsumptionMode,
  useMigrateGroupedTransactionPaymentSplits,
} from '../../hooks/useSuperAdmin';
import { useAuth } from '../../contexts/AuthContext';
import {
  Business,
  BUSINESS_TYPE_CONFIGS,
  BUSINESS_FEATURES,
  BusinessFeatureKey,
  DEFAULT_BUSINESS_CONFIGURATION,
  FEATURE_PARENT_OF,
  FEATURE_REQUIRES_OF,
  getMutualFeaturePeers,
  computeFeatureToggleUpdate,
  getActiveFeatures,
  isFeatureEnabled,
  isInventoryEnabled,
} from '../../types/business';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import relativeTime from 'dayjs/plugin/relativeTime';
import EditBusinessInfoModal from './components/EditBusinessInfoModal';
import EditContactBrandingModal from './components/EditContactBrandingModal';
import EditConfigurationModal, {
  ConfigEditSection,
} from './components/EditConfigurationModal';
import EditTaxModal from './components/EditTaxModal';
import RoleProfilesTabContent from './components/RoleProfilesTabContent';
import SitesTabContent from './components/SitesTabContent';
import LoyaltyPassVisitOutboxTabContent from './components/LoyaltyPassVisitOutboxTabContent';
import ContractTabContent from './components/ContractTabContent';
import ProductsImportExportTabContent from './components/ProductsImportExportTabContent';
import ClientsImportExportTabContent from './components/ClientsImportExportTabContent';
import CategoriesImportExportTabContent from './components/CategoriesImportExportTabContent';
import { cleanFirestoreData } from '../../utils/cleanFirestoreData';
import { stripDisabledFeaturesFromBusinessRoleProfiles } from '../../utils/roleProfileFeatureSync';
import { usePickupCities } from '../../hooks/usePickupCities';
import { useQueryClient } from '@tanstack/react-query';
import './BusinessDetailPage.css';

import { formatNumber } from '../../utils/numberFormat';

dayjs.locale('fr');
dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const TAB_KEYS = [
  'info',
  'config',
  'contract',
  'tax',
  'roles',
  'sites',
  'products-excel',
  'categories-excel',
  'clients-excel',
  'pass-outbox',
] as const;

const BusinessDetailPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const tabFromUrl = searchParams.get('tab');
  const { data: business, isLoading, isError, refetch } = useBusiness(businessId || '');
  const activeTab =
    tabFromUrl && (TAB_KEYS as readonly string[]).includes(tabFromUrl)
      ? tabFromUrl
      : 'info';
  const { switchToBusiness, userProfile, isCSM, isSuperAdmin } = useAuth();
  const canUseBusinessScopedAdminTools =
    isSuperAdmin || (isCSM && !!businessId && (userProfile?.businessIds?.includes(businessId) ?? false));
  const deleteBusinessMutation = useDeleteBusiness();
  const toggleBusinessMutation = useToggleBusinessStatus();
  const toggleLoyaltyMutation = useToggleLoyaltySystem();
  const updateBusiness = useUpdateBusiness();
  const migrateClientLastVisitDateMutation = useMigrateClientLastVisitDate();
  const migrateProductSellingPriceMutation = useMigrateProductSellingPriceFromCost();
  const migratePackagingUnitsMutation = useMigratePackagingUnitsDefaults();
  const removeRecalculateCostMutation = useRemoveRecalculateCostOnProvisioning();
  const backfillServiceStockModeMutation = useBackfillServiceStockConsumptionMode();
  const migrateGroupedPaymentSplitsMutation = useMigrateGroupedTransactionPaymentSplits();
  const { data: pickupCities = [], isLoading: pickupCitiesLoading } = usePickupCities({
    activeOnly: true,
  });
  const hasPickupCities = pickupCities.length > 0;
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [editConfigOpen, setEditConfigOpen] = useState(false);
  const [editConfigSection, setEditConfigSection] = useState<ConfigEditSection>('all');
  const [editTaxOpen, setEditTaxOpen] = useState(false);
  const [migrateLastVisitModalVisible, setMigrateLastVisitModalVisible] = useState(false);

  const openConfigSection = (section: ConfigEditSection) => {
    setEditConfigSection(section);
    setEditConfigOpen(true);
  };

  const configBlockEditButton = (section: ConfigEditSection) => (
    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openConfigSection(section)}>
      Modifier
    </Button>
  );

  const handleViewBusiness = async (b: Business) => {
    try {
      await switchToBusiness(b.id);
      message.success(`Connecté au business: ${b.name}`);
      navigate('/dashboard');
    } catch (error) {
      message.error('Erreur lors de la connexion au business');
      console.error(error);
    }
  };

  const handleToggleLoyalty = async (b: Business) => {
    try {
      const currentEnabled = b.configuration?.loyalty?.enabled !== false;
      const enabling = !currentEnabled;
      if (enabling && isFeatureEnabled(b, 'loyaltyPass')) {
        message.error(
          'Impossible d’activer la cagnotte : la carte de fidélité BonSpot est active. Désactivez d’abord la feature « Carte de fidélité BonSpot ».'
        );
        return;
      }
      await toggleLoyaltyMutation.mutateAsync({
        businessId: b.id,
        enabled: enabling,
      });
      message.success(`Système de cagnotte ${enabling ? 'activé' : 'désactivé'} avec succès`);
      refetch();
    } catch (error) {
      message.error('Erreur lors de la modification du système de cagnotte');
      console.error(error);
    }
  };

  const handleDeleteBusiness = (b: Business) => {
    const { Modal: AntModal } = require('antd');
    AntModal.confirm({
      title: 'Supprimer définitivement ce business ?',
      content: 'Cette action est irréversible. Toutes les données seront perdues.',
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          await deleteBusinessMutation.mutateAsync(b.id);
          message.success('Business supprimé');
          navigate('/super-admin/businesses');
        } catch (error: any) {
          message.error(error.message || 'Erreur lors de la suppression');
        }
      },
    });
  };

  useEffect(() => {
    if (!businessId || isError || (!isLoading && !business)) {
      if (businessId && (isError || !business)) navigate('/super-admin/businesses');
    }
  }, [businessId, isError, isLoading, business, navigate]);

  if (!businessId) return null;

  if (isLoading) {
    return (
      <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (isError || !business) return null;

  const canConnect =
    isSuperAdmin === true ||
    !isCSM ||
    (userProfile?.businessIds?.includes(business.id) ?? false);
  const activeFeatures = getActiveFeatures(business);

  const handleToggleFeature = async (featureKey: BusinessFeatureKey, checked: boolean) => {
    if (featureKey === 'pickup' && checked && !hasPickupCities) {
      message.error(
        'Ajoutez au moins une ville active (Super Admin → Villes & quartiers) avant d’activer le Ramassage.'
      );
      return;
    }
    if (
      featureKey === 'loyaltyPass' &&
      checked &&
      business &&
      business.configuration?.loyalty?.enabled !== false
    ) {
      message.error(
        'Impossible d’activer la carte BonSpot : la cagnotte FCFA est active. Désactivez d’abord le système de cagnotte.'
      );
      return;
    }
    try {
      const previousActive = getActiveFeatures(business);
      const { activeFeatures: next, disabledFeatures } = computeFeatureToggleUpdate(
        business,
        featureKey,
        checked
      );

      const enablingLoyaltyPass = featureKey === 'loyaltyPass' && checked;
      const passProgram = enablingLoyaltyPass
        ? {
            ...normalizeLoyaltyPassProgram(
              business.loyaltyPassProgram ?? defaultLoyaltyPassProgram()
            ),
            enabled: true,
          }
        : undefined;

      await updateBusiness.mutateAsync({
        id: business.id,
        activeFeatures: next,
        disabledFeatures,
        ...(passProgram ? { loyaltyPassProgram: passProgram } : {}),
      });

      if (enablingLoyaltyPass) {
        try {
          const provision = httpsCallable<
            { businessId: string; program?: Record<string, unknown> },
            { synced?: boolean; merchantId?: string; reason?: string }
          >(functions, 'loyaltyPassProvisionMerchant');
          const res = await provision({
            businessId: business.id,
            program: {
              enabled: true,
              visitsRequired: passProgram!.visitsRequired,
              reward: passProgram!.reward,
              ...(passProgram!.cardColor
                ? { cardColor: passProgram!.cardColor }
                : {}),
              rules: passProgram!.rules ?? [],
            },
          });
          const sync = res.data;
          if (sync?.synced && sync.merchantId) {
            await updateBusiness.mutateAsync({
              id: business.id,
              bonspotMerchantId: sync.merchantId,
            });
            message.success(
              'Carte de fidélité BonSpot activée et liée à BonSpot'
            );
          } else if (sync?.reason === 'bridge_not_configured') {
            message.warning(
              'Feature activée. Pont BonSpot non configuré (dev) — liez plus tard depuis Paramètres.'
            );
          } else {
            message.warning(
              'Feature activée. Lien BonSpot incomplet — utilisez « Lier / synchroniser » dans Paramètres Pass.'
            );
          }
        } catch (provisionErr) {
          console.error('[loyaltyPass] provision on feature enable', provisionErr);
          message.warning(
            'Feature activée. Échec du lien BonSpot — réessayez depuis Paramètres → Carte de fidélité BonSpot.'
          );
        }
      }

      const removedFeatures = previousActive.filter((f) => !next.includes(f));
      if (removedFeatures.length > 0) {
        const profilesUpdated = await stripDisabledFeaturesFromBusinessRoleProfiles(
          business.id,
          removedFeatures
        );
        if (profilesUpdated > 0) {
          queryClient.invalidateQueries({ queryKey: ['roleProfiles'] });
          message.success(
            `Features mises à jour. ${profilesUpdated} profil(s) de rôle mis à jour (droits retirés).`
          );
        } else if (!enablingLoyaltyPass) {
          message.success('Features mises à jour avec succès');
        }
      } else if (!enablingLoyaltyPass) {
        message.success('Features mises à jour avec succès');
      }
      refetch();
    } catch (error) {
      console.error('Erreur lors de la mise à jour des features:', error);
      message.error('Erreur lors de la mise à jour des features');
    }
  };

  const handleMigrateClientLastVisitDate = () => {
    Modal.confirm({
      title: 'Migration des dates de dernière visite',
      content: (
        <div>
          <p>Cette migration va remplir le champ <code>lastVisitDate</code> pour tous les clients existants en analysant leurs prestations et ventes produits.</p>
          <p style={{ marginTop: 8, color: '#666' }}>
            <strong>Note :</strong> Pour chaque client, la date de dernière visite sera déterminée en trouvant la transaction la plus récente (prestation ou vente produit non archivée).
          </p>
          <p style={{ marginTop: 8, color: '#666' }}>
            Les clients sans transaction seront ignorés.
          </p>
        </div>
      ),
      okText: 'Lancer la migration',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          setMigrateLastVisitModalVisible(true);
          const result = await migrateClientLastVisitDateMutation.mutateAsync(business.id);
          
          Modal.success({
            title: 'Migration terminée avec succès',
            content: (
              <div>
                <p><strong>Clients mis à jour :</strong> {result.clients.updated}</p>
                <p><strong>Clients ignorés :</strong> {result.clients.skipped} (sans transaction)</p>
              </div>
            ),
            onOk: () => setMigrateLastVisitModalVisible(false),
          });
        } catch (error: any) {
          setMigrateLastVisitModalVisible(false);
          message.error(error.message || 'Erreur lors de la migration');
          console.error('Erreur migration:', error);
        }
      },
    });
  };

  const handleMigrateProductSellingPrice = () => {
    Modal.confirm({
      title: 'Migration prix de vente',
      content: (
        <div>
          <p>Cette migration va définir le <strong>prix de vente</strong> de tous les produits à partir de leur <strong>prix d'achat</strong> (coût).</p>
          <p style={{ marginTop: 8, color: '#666' }}>
            Seuls les produits ayant un prix d'achat défini seront mis à jour. Les produits sans prix d'achat seront ignorés.
          </p>
        </div>
      ),
      okText: 'Lancer la migration',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const result = await migrateProductSellingPriceMutation.mutateAsync(business.id);
          Modal.success({
            title: 'Migration terminée',
            content: <p><strong>{result.updated}</strong> produit(s) mis à jour (prix de vente = prix d'achat).</p>,
          });
        } catch (error: any) {
          message.error(error.message || 'Erreur lors de la migration');
        }
      },
    });
  };

  const handleMigratePackagingUnitsDefaults = () => {
    Modal.confirm({
      title: 'Initialiser les unités de conditionnement',
      content: (
        <div>
          <p>
            Écrit / complète <code>configuration.products.packagingUnits</code> pour ce business avec la
            liste standard (g, kg, L, ml, pièce, carton, Plaquette, boîte(s), comprimé(s)).
          </p>
          <p style={{ marginTop: 8, color: '#666' }}>
            Les unités déjà présentes (y compris custom) sont conservées ; seules les unités standard
            manquantes sont ajoutées. Les produits existants ne sont pas modifiés.
          </p>
        </div>
      ),
      okText: 'Lancer la migration',
      cancelText: 'Annuler',
      okButtonProps: { loading: migratePackagingUnitsMutation.isPending },
      onOk: async () => {
        try {
          const result = await migratePackagingUnitsMutation.mutateAsync(business.id);
          Modal.success({
            title: result.changed ? 'Migration terminée' : 'Déjà à jour',
            content: (
              <div>
                {result.changed ? (
                  <>
                    <p>
                      <strong>Avant :</strong> {result.beforeCount} unité(s) — <strong>Après :</strong>{' '}
                      {result.afterCount} (+{result.added ?? 0})
                    </p>
                    <p style={{ marginTop: 8 }}>{result.units.join(', ')}</p>
                  </>
                ) : (
                  <p>
                    La liste était déjà complète ({result.afterCount} unité(s)) :{' '}
                    {result.units.join(', ')}
                  </p>
                )}
              </div>
            ),
          });
          refetch();
        } catch (error: any) {
          message.error(error.message || 'Erreur lors de la migration');
        }
      },
    });
  };

  const handleRemoveRecalculateCostOnProvisioning = () => {
    Modal.confirm({
      title: 'Retirer le flag recalculateCostOnProvisioning',
      content: (
        <div>
          <p>
            Cette action retire définitivement le champ legacy{' '}
            <code>configuration.inventory.recalculateCostOnProvisioning</code> pour ce business.
          </p>
          <p style={{ marginTop: 8, color: '#666' }}>
            Le recalcul automatique de prix à l&apos;approvisionnement n&apos;est plus géré par l&apos;application.
          </p>
        </div>
      ),
      okText: 'Retirer le champ',
      cancelText: 'Annuler',
      okButtonProps: { loading: removeRecalculateCostMutation.isPending },
      onOk: async () => {
        try {
          const result = await removeRecalculateCostMutation.mutateAsync(business.id);
          Modal.success({
            title: 'Migration terminée',
            content: result.hadField
              ? 'Le champ a été supprimé avec succès pour ce business.'
              : 'Le champ était déjà absent pour ce business.',
          });
          refetch();
        } catch (error: any) {
          message.error(error.message || 'Erreur lors de la migration');
        }
      },
    });
  };

  const handleBackfillServiceStockConsumptionMode = () => {
    Modal.confirm({
      title: 'Migration mode de consommation de stock (services)',
      content: (
        <div>
          <p>
            Met à jour <code>stockConsumptionMode</code> sur les services du business : d’abord les champs
            manquants, puis les incohérences (prestations avec produits consommés à la vente doivent être en{' '}
            <code>direct</code>).
          </p>
          <ul>
            <li>
              Si <code>consumedProducts</code> contient au moins une ligne valide → <strong>direct</strong> (prioritaire,
              comportement historique « débit à la vente »).
            </li>
            <li>
              Sinon, si <code>manufacturingTemplate.defaultLines</code> est renseigné → <code>manufacturing_order</code>
            </li>
            <li>Sinon → <code>none</code></li>
          </ul>
          <p style={{ marginTop: 8 }}>
            Les services qui avaient déjà un mode mais des produits consommés sans <code>direct</code> sont corrigés
            vers <code>direct</code>.
          </p>
        </div>
      ),
      okText: 'Lancer la migration',
      cancelText: 'Annuler',
      okButtonProps: { loading: backfillServiceStockModeMutation.isPending },
      onOk: async () => {
        try {
          const result = await backfillServiceStockModeMutation.mutateAsync(business.id);
          Modal.success({
            title: 'Migration terminée',
            content: (
              <div>
                <p><strong>Services scannés :</strong> {result.scanned}</p>
                <p><strong>Documents mis à jour :</strong> {result.updated}</p>
                <p>
                  <strong>Dont mode absent rempli :</strong> {result.filledMissing} —{' '}
                  <strong>passage en direct (produits à la vente) :</strong> {result.forcedDirect}
                </p>
              </div>
            ),
          });
        } catch (error: any) {
          message.error(error.message || 'Erreur lors de la migration');
        }
      },
    });
  };

  const handleMigrateGroupedTransactionPaymentSplits = () => {
    Modal.confirm({
      title: 'Migration paiements (ventes groupées)',
      content: (
        <div>
          <p>
            Corrige les documents <code>prestations</code> et <code>productSales</code> partageant un{' '}
            <code>transactionGroupId</code> lorsque chaque ligne avait reçu le même{' '}
            <code>paymentSplits</code> que le panier entier (sommes multipliées dans les rapports).
          </p>
          <p style={{ marginTop: 8 }}>
            Seuls les groupes où au moins deux lignes ont des splits identiques et où la somme fusionnée
            dépasse le total TTC du groupe sont mis à jour ; les autres groupes sont ignorés.
          </p>
        </div>
      ),
      okText: 'Lancer la migration',
      cancelText: 'Annuler',
      okButtonProps: { loading: migrateGroupedPaymentSplitsMutation.isPending },
      onOk: async () => {
        try {
          const result = await migrateGroupedPaymentSplitsMutation.mutateAsync(business.id);
          Modal.success({
            title: 'Migration terminée',
            content: (
              <div>
                <p>
                  <strong>Groupes scannés :</strong> {result.groupsScanned} —{' '}
                  <strong>groupes corrigés :</strong> {result.groupsFixed} —{' '}
                  <strong>ignorés :</strong> {result.groupsSkipped}
                </p>
                <p>
                  <strong>Lignes mises à jour :</strong> {result.linesUpdated} (prestations :{' '}
                  {result.prestationsUpdated}, ventes produits : {result.productSalesUpdated})
                </p>
              </div>
            ),
          });
        } catch (error: any) {
          message.error(error.message || 'Erreur lors de la migration');
        }
      },
    });
  };

  const tabItems = [
    {
      key: 'info',
      label: 'Informations générales',
      children: (
        <div className="business-detail-tab">
          {/* Actions */}
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <Button type="primary" icon={<EditOutlined />} onClick={() => setEditInfoOpen(true)}>
              Modifier les infos
            </Button>
            <Button icon={<EditOutlined />} onClick={() => setEditContactOpen(true)}>
              Modifier contact & branding
            </Button>
            <Button 
              icon={<SyncOutlined />} 
              onClick={handleMigrateClientLastVisitDate}
              loading={migrateClientLastVisitDateMutation.isPending}
              style={{ borderColor: '#1890ff', color: '#1890ff' }}
            >
              Migration lastVisitDate
            </Button>
            <Button 
              icon={<SyncOutlined />} 
              onClick={handleMigrateProductSellingPrice}
              loading={migrateProductSellingPriceMutation.isPending}
              style={{ borderColor: '#52c41a', color: '#52c41a' }}
            >
              Migration prix vente
            </Button>
            <Button
              icon={<SyncOutlined />}
              onClick={handleMigratePackagingUnitsDefaults}
              loading={migratePackagingUnitsMutation.isPending}
              disabled={!isSuperAdmin}
              style={{ borderColor: '#13c2c2', color: '#13c2c2' }}
            >
              Init. unités conditionnement
            </Button>
            <Button
              icon={<SyncOutlined />}
              onClick={handleRemoveRecalculateCostOnProvisioning}
              loading={removeRecalculateCostMutation.isPending}
              style={{ borderColor: '#eb2f96', color: '#eb2f96' }}
            >
              Retirer recalculateCostOnProvisioning
            </Button>
            <Button
              icon={<SyncOutlined />}
              onClick={handleBackfillServiceStockConsumptionMode}
              loading={backfillServiceStockModeMutation.isPending}
              style={{ borderColor: '#722ed1', color: '#722ed1' }}
            >
              Migration mode stock services
            </Button>
            <Button
              icon={<SyncOutlined />}
              onClick={handleMigrateGroupedTransactionPaymentSplits}
              loading={migrateGroupedPaymentSplitsMutation.isPending}
              style={{ borderColor: '#fa8c16', color: '#fa8c16' }}
            >
              Migration splits paiement (groupées)
            </Button>
          </div>

          {/* Vue d'ensemble */}
          <Card title="Vue d'ensemble" className="info-card">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <div className="info-item">
                  <Text type="secondary" className="info-label">Type de business</Text>
                  <div style={{ marginTop: 4 }}>
                    <Tag color="blue" icon={<TagOutlined />}>
                      {BUSINESS_TYPE_CONFIGS[business.type]?.name || business.type}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="info-item">
                  <Text type="secondary" className="info-label">Statut</Text>
                  <div style={{ marginTop: 4 }}>
                    <Tag color={business.isActive ? 'success' : 'error'} icon={business.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                      {business.isActive ? 'Actif' : 'Inactif'}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="info-item">
                  <Text type="secondary" className="info-label">Configuration</Text>
                  <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color={business.configurationInProgress ? 'orange' : 'green'} icon={business.configurationInProgress ? <WarningOutlined /> : <CheckCircleOutlined />}>
                      {business.configurationInProgress ? 'En cours' : 'Prête'}
                    </Tag>
                    {(isSuperAdmin || isCSM) && (
                      <Switch
                        checked={business.configurationInProgress}
                        onChange={async (checked) => {
                          try {
                            await updateBusiness.mutateAsync({
                              id: business.id,
                              configurationInProgress: checked,
                            });
                            message.success(
                              checked
                                ? 'Le business est maintenant marqué comme "en cours de configuration"'
                                : 'Le business est maintenant marqué comme "prêt"'
                            );
                            refetch();
                          } catch (error) {
                            console.error('Erreur lors de la mise à jour du statut de configuration:', error);
                            message.error('Erreur lors de la mise à jour du statut de configuration');
                          }
                        }}
                        size="small"
                      />
                    )}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="info-item">
                  <Text type="secondary" className="info-label">Créé le</Text>
                  <div style={{ marginTop: 4 }}>
                    <Space size={4}>
                      <ClockCircleOutlined />
                      <Text>{dayjs(business.createdAt).format('DD/MM/YYYY')}</Text>
                      <Text type="secondary">({dayjs(business.createdAt).fromNow()})</Text>
                    </Space>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={24} md={16}>
                <div className="info-item">
                  <Text type="secondary" className="info-label">ID Business</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text code copyable style={{ fontSize: 12 }}>{business.id}</Text>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Contact & Branding */}
          {business.branding && (
            <>
              <Card title="📱 Contact & branding" className="info-card" style={{ marginTop: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={8}>
                    <div className="info-item">
                      <Space size={4}>
                        <PhoneOutlined style={{ color: '#1890ff' }} />
                        <Text type="secondary" className="info-label">Téléphone</Text>
                      </Space>
                      <div style={{ marginTop: 4 }}>
                        <Text code copyable>{business.branding.phone}</Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <div className="info-item">
                      <Space size={4}>
                        <PhoneOutlined style={{ color: '#25D366' }} />
                        <Text type="secondary" className="info-label">WhatsApp</Text>
                      </Space>
                      <div style={{ marginTop: 4 }}>
                        <Text code copyable>{business.branding.whatsappNumber}</Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <div className="info-item">
                      <Space size={4}>
                        <MailOutlined style={{ color: '#722ed1' }} />
                        <Text type="secondary" className="info-label">Nom SMS</Text>
                      </Space>
                      <div style={{ marginTop: 4 }}>
                        <Text code>{business.branding.smsSenderName}</Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24}>
                    <div className="info-item">
                      <Space size={4}>
                        <EnvironmentOutlined style={{ color: '#ff4d4f' }} />
                        <Text type="secondary" className="info-label">Adresse</Text>
                      </Space>
                      <div style={{ marginTop: 4 }}>
                        <Text>{business.branding.address}</Text>
                      </div>
                    </div>
                  </Col>
                  {business.branding.description && (
                    <Col xs={24}>
                      <div className="info-item">
                        <Space size={4}>
                          <FileTextOutlined style={{ color: '#8c8c8c' }} />
                          <Text type="secondary" className="info-label">Description</Text>
                        </Space>
                        <div style={{ marginTop: 4 }}>
                          <Text>{business.branding.description}</Text>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card>

              <Card title="🎨 Personnalisation" className="info-card" style={{ marginTop: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <div className="info-item">
                      <Text type="secondary" className="info-label">Couleur primaire</Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                        <div className="color-preview" style={{ backgroundColor: business.branding.primaryColor || '#667eea' }} />
                        <Text code>{business.branding.primaryColor || '#667eea'}</Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div className="info-item">
                      <Text type="secondary" className="info-label">Couleur secondaire</Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                        <div className="color-preview" style={{ backgroundColor: business.branding.secondaryColor || '#764ba2' }} />
                        <Text code>{business.branding.secondaryColor || '#764ba2'}</Text>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'config',
      label: 'Configuration',
      children: business.configuration ? (
        <div className="business-detail-tab">
          {/* Actions générales */}
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" icon={<EditOutlined />} onClick={() => openConfigSection('all')}>
              Tout modifier
            </Button>
          </div>

          {/* Programme de fidélité */}
          <Card
            title="💎 Programme de fidélité"
            className="info-card"
            style={{ marginBottom: 16 }}
            extra={configBlockEditButton('loyalty')}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <div className="metric-card">
                  <div className="metric-value" style={{ color: '#1890ff' }}>
                    {business.configuration.loyalty?.bonusPercentage ?? 3}%
                  </div>
                  <Text type="secondary" className="metric-label">Bonus fidélité</Text>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div className="metric-card">
                  <div className="metric-value" style={{ color: '#52c41a' }}>
                    {business.configuration.loyalty?.minimumUsage ?? 1000}
                  </div>
                  <Text type="secondary" className="metric-label">Seuil cagnotte (FCFA)</Text>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div
                  className="metric-card"
                  style={{
                    background:
                      business.configuration?.loyalty?.enabled !== false ? '#f6ffed' : '#fff1f0',
                  }}
                >
                  <Switch
                    checked={business.configuration?.loyalty?.enabled !== false}
                    onChange={() => handleToggleLoyalty(business)}
                    loading={toggleLoyaltyMutation.isPending}
                    disabled={isFeatureEnabled(business, 'loyaltyPass')}
                    checkedChildren="Activé"
                    unCheckedChildren="Désactivé"
                    style={{ marginBottom: 8 }}
                  />
                  <Text type="secondary" className="metric-label">Système de cagnotte</Text>
                  {isFeatureEnabled(business, 'loyaltyPass') && (
                    <Text type="secondary" style={{ display: 'block', fontSize: 11, marginTop: 4 }}>
                      Mutex : carte BonSpot active — désactivez la feature pour réactiver la cagnotte.
                    </Text>
                  )}
                </div>
              </Col>
            </Row>
          </Card>

          {/* Marketing */}
          <Card
            title="📢 Marketing"
            className="info-card"
            style={{ marginBottom: 16 }}
            extra={configBlockEditButton('marketing')}
          >
            <div className="info-item">
              <Text type="secondary" className="info-label">Campagnes actives max</Text>
              <div style={{ marginTop: 8 }}>
                <Tag color="orange" style={{ fontSize: '16px', padding: '8px 16px' }}>
                  {business.configuration.marketing?.maxActiveCampaigns ?? '∞'}
                </Tag>
              </div>
            </div>
          </Card>

          {/* Messages clients */}
          <Card
            title="💬 Messages clients"
            className="info-card"
            style={{ marginBottom: 16 }}
            extra={configBlockEditButton('messagesClients')}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <div className="metric-card">
                  <div className="metric-value" style={{ color: '#1890ff' }}>
                    {business.configuration.messagesClients?.delayMinutes ?? 15} min
                  </div>
                  <Text type="secondary" className="metric-label">Délai avant envoi</Text>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div className="metric-card">
                  <div className="metric-value" style={{ color: '#fa8c16' }}>
                    {business.configuration.messagesClients?.antiSpamHours ?? 24} h
                  </div>
                  <Text type="secondary" className="metric-label">Anti-spam</Text>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div className="metric-card">
                  <div className="metric-value" style={{ color: '#13c2c2', fontSize: '16px' }}>
                    {business.configuration.messagesClients?.requireOptIn !== false ? '✓' : '✗'}
                  </div>
                  <Text type="secondary" className="metric-label">Opt-in client requis</Text>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Inventaire / stocks */}
          {business.configuration.inventory && (
            <Card
              title="📦 Gestion des stocks"
              className="info-card"
              style={{ marginBottom: 16 }}
              extra={configBlockEditButton('inventory')}
            >
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Le module Stock / Inventaire est piloté par les features du business"
                description="Les paramètres affichés ici règlent le comportement du stock (seuil, multi-lieux, dépôt-vente), mais n’activent pas le module à eux seuls."
              />
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <div
                    className="metric-card"
                    style={{
                      background: isInventoryEnabled(business) ? '#f6ffed' : '#fff1f0',
                    }}
                  >
                    <div
                      className="metric-value"
                      style={{ color: isInventoryEnabled(business) ? '#52c41a' : '#ff4d4f' }}
                    >
                      {isInventoryEnabled(business) ? '✓' : '✗'}
                    </div>
                    <Text type="secondary" className="metric-label">Feature Stock / Inventaire</Text>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div className="metric-card">
                    <div className="metric-value" style={{ color: '#1890ff' }}>
                      {business.configuration.inventory.lowStockThreshold ?? 5}
                    </div>
                    <Text type="secondary" className="metric-label">Seuil d&apos;alerte (unités)</Text>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div className="metric-card">
                    <div className="metric-value" style={{ color: '#52c41a', fontSize: '16px' }}>
                      {business.configuration.inventory.recalculateCostOnProvisioning === true
                        ? '✓'
                        : '✗'}
                    </div>
                    <Text type="secondary" className="metric-label">
                      Recalcul auto prix d&apos;achat
                    </Text>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div
                    className="metric-card"
                    style={{
                      background: business.configuration.inventory.multiSitesEnabled
                        ? '#f6ffed'
                        : '#fff1f0',
                    }}
                  >
                    <div
                      className="metric-value"
                      style={{
                        color: business.configuration.inventory.multiSitesEnabled
                          ? '#52c41a'
                          : '#ff4d4f',
                        fontSize: '16px',
                      }}
                    >
                      {business.configuration.inventory.multiSitesEnabled === true ? '✓' : '✗'}
                    </div>
                    <Text type="secondary" className="metric-label">Stock multi-sites</Text>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div className="metric-card">
                    <div className="metric-value" style={{ color: '#722ed1', fontSize: '16px' }}>
                      {business.configuration.inventory.salesSiteMode === 'any_site'
                        ? 'Tous les lieux'
                        : 'Boutique seule'}
                    </div>
                    <Text type="secondary" className="metric-label">
                      Où peut-on vendre ?
                    </Text>
                  </div>
                </Col>
              </Row>
            </Card>
          )}

          {/* Types de clients */}
          <Card
            title="👥 Types de clients"
            className="info-card"
            style={{ marginBottom: 16 }}
            extra={configBlockEditButton('clientTypes')}
          >
            {business.configuration.salesActivity?.clientTypes?.length ? (
              <Space wrap>
                {business.configuration.salesActivity.clientTypes.map((label: string, index: number) => (
                  <Tag key={index} color="blue" className="feature-tag">
                    <UserOutlined /> {label}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Empty description="Aucun type configuré" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          {/* Types d'activité */}
          <Card
            title="📋 Types d'activité"
            className="info-card"
            style={{ marginBottom: 16 }}
            extra={configBlockEditButton('activityTypes')}
          >
            {business.configuration.activityReport?.activityTypes?.length ? (
              <Space wrap>
                {business.configuration.activityReport.activityTypes.map(
                  (label: string, index: number) => (
                    <Tag key={index} color="green" className="feature-tag">
                      <FileTextOutlined /> {label}
                    </Tag>
                  )
                )}
              </Space>
            ) : (
              <Empty description="Aucun type configuré" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          {/* Catégories d'opportunités */}
          <Card
            title="🎯 Catégories d'opportunités"
            className="info-card"
            style={{ marginBottom: 16 }}
            extra={configBlockEditButton('opportunityCategories')}
          >
            {business.configuration.salesActivity?.opportunityCategories?.length ? (
              <Space wrap>
                {business.configuration.salesActivity.opportunityCategories.map(
                  (label: string, index: number) => (
                    <Tag key={index} color="purple" className="feature-tag">
                      <TagOutlined /> {label}
                    </Tag>
                  )
                )}
              </Space>
            ) : (
              <Empty description="Aucune catégorie configurée" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          {/* Catégories de dépenses */}
          <Card
            title="💰 Catégories de dépenses"
            className="info-card"
            style={{ marginBottom: 16 }}
            extra={configBlockEditButton('expenses')}
          >
            {business.configuration.expenses?.categories?.length ? (
              <Space wrap>
                {business.configuration.expenses.categories.map(
                  (cat: { id: string; name: string; color?: string }) => (
                    <Tag key={cat.id} color={cat.color || 'default'} className="feature-tag">
                      <DollarOutlined /> {cat.name}
                    </Tag>
                  )
                )}
              </Space>
            ) : (
              <Empty
                description="Aucune catégorie personnalisée"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>

          {/* Inventaires physiques */}
          {business.configuration.inventoryAudit && (
            <Card title="🔍 Inventaires physiques" className="info-card" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <div className="metric-card">
                    <div className="metric-value" style={{ color: '#1890ff' }}>
                      {business.configuration.inventoryAudit.significantVarianceThreshold ?? 5}%
                    </div>
                    <Text type="secondary" className="metric-label">Seuil d&apos;écart</Text>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="metric-card">
                    <div className="metric-value" style={{ color: '#52c41a' }}>
                      {formatNumber((
                        business.configuration.inventoryAudit.significantValueThreshold ?? 10000
                      ), 0)}
                    </div>
                    <Text type="secondary" className="metric-label">Valeur seuil (FCFA)</Text>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="metric-card">
                    <div className="metric-value" style={{ color: '#722ed1', fontSize: '14px' }}>
                      {business.configuration.inventoryAudit.recommendedFrequency === 'annual'
                        ? 'Annuelle'
                        : business.configuration.inventoryAudit.recommendedFrequency === 'biannual'
                          ? 'Semestrielle'
                          : business.configuration.inventoryAudit.recommendedFrequency ===
                              'quarterly'
                            ? 'Trimestrielle'
                            : business.configuration.inventoryAudit.recommendedFrequency ===
                                'monthly'
                              ? 'Mensuelle'
                              : 'Annuelle'}
                    </div>
                    <Text type="secondary" className="metric-label">Fréquence recommandée</Text>
                  </div>
                </Col>
              </Row>
            </Card>
          )}

          {/* Features actives */}
          <Card title="🧩 Features actives (contrat)" className="info-card" style={{ marginTop: 16 }}>
            <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>
              Cochez les modules disponibles pour ce business. Seules les features actives seront
              visibles dans la configuration des rôles et dans le menu.
            </Text>
            <Row gutter={[16, 12]}>
              {BUSINESS_FEATURES.map((feature) => {
                const parentKey = FEATURE_PARENT_OF[feature.key];
                const requiredRaw = FEATURE_REQUIRES_OF[feature.key];
                const requiredKeys = requiredRaw
                  ? Array.isArray(requiredRaw)
                    ? requiredRaw
                    : [requiredRaw]
                  : [];
                const isCoupledChild = !!parentKey;
                const missingRequired =
                  requiredKeys.length > 0 &&
                  requiredKeys.some((k) => !activeFeatures.includes(k));
                const pickupBlockedByCities =
                  feature.key === 'pickup' &&
                  !activeFeatures.includes('pickup') &&
                  (!hasPickupCities || pickupCitiesLoading);
                const loyaltyPassBlockedByCagnotte =
                  feature.key === 'loyaltyPass' &&
                  !activeFeatures.includes('loyaltyPass') &&
                  business.configuration?.loyalty?.enabled !== false;
                const hasMutualPeer = getMutualFeaturePeers(feature.key).length > 0;
                const showHint = Boolean(
                  feature.description &&
                    (isCoupledChild ||
                      hasMutualPeer ||
                      requiredKeys.length > 0 ||
                      feature.key === 'pickup' ||
                      feature.key === 'loyaltyPass')
                );
                const pickupHint =
                  feature.key === 'pickup' && pickupBlockedByCities
                    ? 'Impossible sans au moins une ville active (menu Villes & quartiers).'
                    : feature.key === 'loyaltyPass' && loyaltyPassBlockedByCagnotte
                      ? 'Impossible tant que la cagnotte FCFA est active. Désactivez d’abord le système de cagnotte.'
                      : feature.description;
                return (
                  <Col key={feature.key} xs={24} sm={12} md={8}>
                    <div className="feature-checkbox-card">
                      <Checkbox
                        checked={activeFeatures.includes(feature.key)}
                        disabled={
                          isCoupledChild ||
                          (missingRequired && !activeFeatures.includes(feature.key)) ||
                          pickupBlockedByCities ||
                          loyaltyPassBlockedByCagnotte
                        }
                        onChange={(e) =>
                          handleToggleFeature(feature.key as BusinessFeatureKey, e.target.checked)
                        }
                      >
                        <Text strong>{feature.label}</Text>
                        {showHint ? (
                          <Text
                            type="secondary"
                            style={{ display: 'block', fontSize: 12, fontWeight: 400 }}
                          >
                            {pickupHint}
                          </Text>
                        ) : null}
                      </Checkbox>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Card>
        </div>
      ) : null,
    },
    {
      key: 'contract',
      label: 'Contrat',
      children: (isSuperAdmin || isCSM) ? (
        <div className="business-detail-tab">
          <ContractTabContent business={business} onRefetch={() => refetch()} />
        </div>
      ) : (
        <Card>
          <Empty description="Réservé au Super Admin et CSM" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Card>
      ),
    },
    {
      key: 'tax',
      label: 'TVA',
      children: business.configuration?.tax ? (
        <div className="business-detail-tab">
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" icon={<EditOutlined />} onClick={() => setEditTaxOpen(true)}>
              Modifier
            </Button>
          </div>
          <Card title="Configuration TVA" className="info-card">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div className="info-item">
                  <Text type="secondary" className="info-label">Mode d'affichage</Text>
                  <div style={{ marginTop: 4 }}>
                    <Tag color="blue" style={{ fontSize: '14px', padding: '6px 12px' }}>
                      {business.configuration.tax.displayMode === 'inclusive' ? '💰 Prix TTC' : '💵 Prix HT'}
                    </Tag>
                  </div>
                </div>
              </Col>
              {business.configuration.tax.defaultRateId && (
                <Col xs={24} sm={12}>
                  <div className="info-item">
                    <Text type="secondary" className="info-label">Taux par défaut</Text>
                    <div style={{ marginTop: 4 }}>
                      {(() => {
                        const tax = business.configuration?.tax;
                        if (!tax) return null;
                        const defaultRate = tax.rates?.find(
                          (r: { id?: string }) => r.id === tax.defaultRateId
                        );
                        return defaultRate ? (
                          <Tag color="purple" style={{ fontSize: '14px', padding: '6px 12px' }}>
                            {defaultRate.name}: {defaultRate.rate}%
                          </Tag>
                        ) : (
                          <Text code>{tax.defaultRateId}</Text>
                        );
                      })()}
                    </div>
                  </div>
                </Col>
              )}
              {business.configuration.tax.rates?.length > 0 && (
                <Col span={24}>
                  <div className="info-item">
                    <Text type="secondary" className="info-label">Taux configurés</Text>
                    <div style={{ marginTop: 8 }}>
                      <Space wrap>
                        {business.configuration.tax.rates.map((rate: { name?: string; rate?: number }, index: number) => (
                          <Tag key={index} color="green" className="feature-tag">
                            {rate.name}: {rate.rate}%
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </Card>
        </div>
      ) : (
        <div className="business-detail-tab">
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" icon={<EditOutlined />} onClick={() => setEditTaxOpen(true)}>
              Modifier
            </Button>
          </div>
          <Card>
            <Empty description="Aucune configuration TVA" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </Card>
        </div>
      ),
    },
    {
      key: 'roles',
      label: 'Profils de rôle',
      children: (
        <div className="business-detail-tab">
          <RoleProfilesTabContent businessId={business.id} businessName={business.name} />
        </div>
      ),
    },
    {
      key: 'sites',
      label: 'Lieux',
      children: (
        <div className="business-detail-tab">
          <SitesTabContent businessId={business.id} />
        </div>
      ),
    },
    {
      key: 'products-excel',
      label: 'Produits (Excel)',
      children: (
        <div className="business-detail-tab">
          <ProductsImportExportTabContent
            businessId={business.id}
            businessName={business.name}
            canUse={canUseBusinessScopedAdminTools}
            inventoryEnabled={isInventoryEnabled(business)}
            multiSitesEnabled={business.configuration?.inventory?.multiSitesEnabled === true}
            defaultMinStockAlert={
              business.configuration?.inventory?.lowStockThreshold ??
              DEFAULT_BUSINESS_CONFIGURATION.inventory.lowStockThreshold
            }
          />
        </div>
      ),
    },
    {
      key: 'categories-excel',
      label: 'Catégories (Excel)',
      children: (
        <div className="business-detail-tab">
          <CategoriesImportExportTabContent
            businessId={business.id}
            businessName={business.name}
            canUse={canUseBusinessScopedAdminTools}
            inventoryEnabled={isInventoryEnabled(business)}
          />
        </div>
      ),
    },
    {
      key: 'clients-excel',
      label: 'Clients (Excel)',
      children: (
        <div className="business-detail-tab">
          <ClientsImportExportTabContent
            businessId={business.id}
            businessName={business.name}
            canUse={canUseBusinessScopedAdminTools}
          />
        </div>
      ),
    },
    {
      key: 'pass-outbox',
      label: 'File Pass BonSpot',
      children: (
        <LoyaltyPassVisitOutboxTabContent
          businessId={business.id}
          loyaltyPassEnabled={isFeatureEnabled(business, 'loyaltyPass')}
          canRetry={isSuperAdmin === true}
        />
      ),
    },
  ];

  return (
    <div className="business-detail-page">
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/super-admin/businesses">Administration</Link> },
          { title: <Link to="/super-admin/businesses">Business</Link> },
          { title: business.name },
        ]}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShopOutlined style={{ color: '#722ed1' }} />
            {business.name}
          </Title>
          <Space style={{ marginTop: 8 }} wrap>
            <Tag color="blue" icon={<TagOutlined />}>{BUSINESS_TYPE_CONFIGS[business.type]?.name || business.type}</Tag>
            <Tag color={business.isActive ? 'success' : 'error'} icon={business.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
              {business.isActive ? 'Actif' : 'Inactif'}
            </Tag>
          </Space>
        </div>
        <Space wrap>
          <Tooltip title={!canConnect ? "Vous n'êtes pas lié à ce business" : undefined}>
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => handleViewBusiness(business)}
              disabled={!canConnect}
              style={canConnect ? { backgroundColor: '#52c41a', borderColor: '#52c41a' } : undefined}
            >
              Se connecter
            </Button>
          </Tooltip>
          {!business.isActive && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteBusiness(business)}
              loading={deleteBusinessMutation.isPending}
            >
              Supprimer
            </Button>
          )}
        </Space>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setSearchParams(key ? { tab: key } : {})}
        items={tabItems}
        className="business-detail-tabs"
      />

      <EditBusinessInfoModal
        open={editInfoOpen}
        business={business}
        onClose={() => setEditInfoOpen(false)}
        onSuccess={() => refetch()}
      />
      <EditContactBrandingModal
        open={editContactOpen}
        business={business}
        onClose={() => setEditContactOpen(false)}
        onSuccess={() => refetch()}
      />
      <EditConfigurationModal
        open={editConfigOpen}
        business={business}
        section={editConfigSection}
        onClose={() => {
          setEditConfigOpen(false);
          setEditConfigSection('all');
        }}
        onSuccess={async () => {
          await refetch();
        }}
      />
      <EditTaxModal
        open={editTaxOpen}
        business={business}
        onClose={() => setEditTaxOpen(false)}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default BusinessDetailPage;
